import React from "react";
import {useTranslation} from "react-i18next";
import {FaIcon} from "../FaIcon";

/**
 * Aviso al configurar el presupuesto de una línea de control cuyas cuentas publicitarias operan
 * en otra moneda: con esa combinación, los importes se van a mostrar convertidos.
 *
 * Reusa el lenguaje visual de `MixedCurrencyWarning` del Explorador (banner ámbar inline, no
 * bloqueante). Acá el ámbar SÍ corresponde: el usuario está por elegir algo que después le va a
 * hacer ruido, a diferencia del tablero donde la conversión ya es un hecho y solo se informa.
 *
 * `linkedAccountCurrencies`: [{currency, rate_to_budget_currency}] — todas las monedas de las
 * cuentas vinculadas. El rate está expresado contra `originalCurrency` (la moneda con la que la
 * línea vino del backend), que es lo que permite sugerir el monto equivalente al cambiarla.
 */
export function CurrencyMismatchNotice({selectedCurrency, originalCurrency, allocatedBudget, linkedAccountCurrencies}) {
    const {t} = useTranslation();

    if (!selectedCurrency || !Array.isArray(linkedAccountCurrencies) || linkedAccountCurrencies.length === 0) {
        return null;
    }

    const currenciesToConvert = linkedAccountCurrencies
        .map(linkedCurrency => linkedCurrency.currency)
        .filter(currency => currency !== selectedCurrency);

    if (currenciesToConvert.length === 0) {
        return null;
    }

    const equivalentAmountText = buildEquivalentAmountText({
        selectedCurrency, originalCurrency, allocatedBudget, linkedAccountCurrencies, t
    });

    // Si el usuario todavía no tocó el selector, el mismatch ya existía: es información, no una
    // advertencia. El ámbar se reserva para cuando está por introducirlo él (severidad por color:
    // si todo es ámbar, nada lo es).
    const isIntroducingTheMismatch = Boolean(originalCurrency) && selectedCurrency !== originalCurrency;

    return (
        <div className={`mixed-currency-warning ${isIntroducingTheMismatch ? '' : '-info'}`} role="status">
            <FaIcon iconName={isIntroducingTheMismatch ? 'triangle-exclamation' : 'circle-info'}
                    className="mixed-currency-warning__icon"/>
            <span className="mixed-currency-warning__text">
                {t('budget_config.currency_mismatch_notice', {
                    currencies: currenciesToConvert.join(' · '),
                    selected: selectedCurrency,
                })}
                {equivalentAmountText ? ` ${equivalentAmountText}` : null}
            </span>
        </div>
    );
}

function buildEquivalentAmountText({selectedCurrency, originalCurrency, allocatedBudget, linkedAccountCurrencies, t}) {
    if (!originalCurrency || selectedCurrency === originalCurrency) {
        return null;
    }
    const amount = Number(allocatedBudget);
    if (!amount) {
        return null;
    }
    const selectedEntry = linkedAccountCurrencies.find(linkedCurrency => linkedCurrency.currency === selectedCurrency);
    if (!selectedEntry || !selectedEntry.rate_to_budget_currency) {
        return null;
    }

    return t('budget_config.currency_change_amount_hint', {
        original_amount: formatAmount(amount),
        original_currency: originalCurrency,
        converted_amount: formatAmount(amount / selectedEntry.rate_to_budget_currency),
        selected_currency: selectedCurrency,
    });
}

function formatAmount(amount) {
    try {
        return amount.toLocaleString('es-AR', {maximumFractionDigits: 0});
    } catch (e) {
        return String(Math.round(amount));
    }
}
